import './bootstrap';
import '../css/app.css';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { createInertiaApp, router } from '@inertiajs/react';
import { RouteContext } from '@/Hooks/useRoute';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import * as Sentry from '@sentry/react';
import { LanguageProvider } from './lib/LanguageProviders';
import { startAnalytics } from './lib/analytics';
import { followConsulIdentity } from './lib/consul/identity';
import pageTitle from './lib/pageTitle';
import { installPopstateGate } from './lib/navigationGuard';
import whenPageSettled from './lib/whenPageSettled';
// Listens for history restores from the start: a page that reloads on Back (Insights) is a lazy
// chunk, fetched after the popstate it needs to have heard.
import './lib/historyRestore';

const posthogoptions = {
  api_host: import.meta.env.VITE_APP_PUBLIC_POSTHOG_HOST,
}

// Before Inertia's own popstate listener, so a page's guard runs first (lib/navigationGuard).
installPopstateGate();

createInertiaApp({
  title: pageTitle,
  progress: {
    color: '#4B5563',
  },
  resolve: name =>
    resolvePageComponent(
      `./Pages/${name}.jsx`,
      import.meta.glob('./Pages/**/*.jsx'),
    ),
  setup({ el, App, props }) {
    const root = createRoot(el);
    Sentry.init({
      dsn: "https://b200ca7d8941db719651b976ea2187ec@sentry.leventdev.com/3",
      // The git tag, baked in at build time by deployment/ECRAppImageDockerfile.
      // Must stay byte-identical to the backend's SENTRY_RELEASE — a browser
      // error and a server error from the same deploy landing under different
      // release names is the failure this is guarding against. Undefined in dev,
      // which Sentry reads as "no release" rather than as an error.
      release: import.meta.env.VITE_SENTRY_RELEASE,
      // Shared by the server at run time (HandleInertiaRequests) rather than
      // baked into the build, so a build reports whichever environment serves
      // it. Left unset, the SDK reports every event as "production".
      environment: props.initialPage.props.sentryEnvironment,
      // Relay events through our own origin so ad blockers / CORS can't drop them.
      tunnel: "/api/tunnel",
      integrations: [
        Sentry.browserTracingIntegration(),
        // Replay is added once the page has settled (below), and the
        // bug report form by the sidebar's "Report a Bug" entry.
      ],
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      tracePropagationTargets: ["localhost", /^https:\/\/synex\.test/, /^https:\/\/(?:[a-z0-9-]+\.)?synexcloud\.com(?=[/:?#]|$)/],
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 0.1 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur
    })
    // Session replay's recorder is a chunk of its own (sentryReplay.js),
    // off the first paint's critical path; the sample rates above still apply.
    whenPageSettled(() => import('./lib/sentryReplay')
      .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration()))
      .catch(() => {}));
    startAnalytics(import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY, posthogoptions);
    // Consul belongs to whoever is signed in, on their current team: a sign-out,
    // a sign-in or a team switch with no page load lets the last one's go.
    followConsulIdentity(router, props.initialPage);
    return root.render(
      <RouteContext.Provider value={window.route}>
        <LanguageProvider>
          <App {...props} />
        </LanguageProvider>
      </RouteContext.Provider>,
    );
  },
});
